import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, Loader2 } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useApp } from "../lib/AppContext";

export default function Login() {
  const { setUser } = useApp();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [mfa, setMfa] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const r = await api.post("/auth/login", { email, password, mfa_code: mfa ? code : undefined });
      if (r.data.mfa_required) { setMfa(true); return; }
      setUser(r.data);
      const next = new URLSearchParams(window.location.search).get("next") || "/";
      if (r.data.role === "member") nav("/member");
      else nav(next.startsWith("/") && !next.startsWith("//") ? next : "/");
    } catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  };

  return (
    <div className="grain grid min-h-screen bg-white lg:grid-cols-[1.2fr_1fr]">
      <div className="relative hidden flex-col justify-between overflow-hidden border-r border-[#E2E8F0] p-10 lg:flex">
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "linear-gradient(#2563EB 1px, transparent 1px), linear-gradient(90deg, #2563EB 1px, transparent 1px)", backgroundSize: "48px 48px" }} />
        <div className="relative flex items-center gap-2 font-display text-sm font-extrabold tracking-tight"><img src="/logo.svg" alt="DMI Share Investment" className="h-9 w-9" data-testid="login-logo" />DMI <span className="text-blue-600">SHARE</span> INVESTMENT</div>
        <div className="relative max-w-lg">
          <h1 className="font-display text-4xl font-extrabold leading-tight sm:text-5xl lg:text-6xl">Capital first.<br /><span className="text-blue-500">Trades second.</span></h1>
          <p className="mt-5 text-sm text-slate-600">Private autonomous paper-trading terminal for Bursa Malaysia, US and Hong Kong equities. Every order passes the Capital Shield. The AI is allowed to say NO TRADE.</p>
          <div className="mono mt-8 grid grid-cols-3 gap-3 text-[11px]">
            {[["0.25%", "risk / trade"], ["1% · 3%", "daily · weekly lock"], ["2:1", "min reward:risk"]].map(([a, b]) => (
              <div key={b} className="panel p-3"><div className="text-lg text-slate-900">{a}</div><div className="text-slate-500">{b}</div></div>
            ))}
          </div>
        </div>
        <div className="relative text-[10px] text-slate-600">Trading involves risk of loss. No profits are guaranteed.</div>
      </div>
      <div className="flex items-center justify-center p-6">
        <form onSubmit={submit} className="panel w-full max-w-sm p-6 fade-up" data-testid="login-form">
          <div className="mb-6 flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-blue-500" /><h2 className="font-display text-lg font-bold">Sign in</h2></div>
          {!mfa ? (
            <>
              <label className="text-[10px] uppercase tracking-widest text-slate-500">Email</label>
              <input data-testid="login-email-input" value={email} onChange={(e) => setEmail(e.target.value)} type="email" required className="mono mb-4 mt-1 w-full rounded-sm border border-[#E2E8F0] bg-white px-3 py-2 text-sm outline-none focus:border-blue-600" />
              <label className="text-[10px] uppercase tracking-widest text-slate-500">Password</label>
              <input data-testid="login-password-input" value={password} onChange={(e) => setPassword(e.target.value)} type="password" required className="mono mb-5 mt-1 w-full rounded-sm border border-[#E2E8F0] bg-white px-3 py-2 text-sm outline-none focus:border-blue-600" />
            </>
          ) : (
            <>
              <label className="text-[10px] uppercase tracking-widest text-slate-500">Authenticator code (MFA)</label>
              <input data-testid="login-mfa-input" value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" maxLength={6} autoFocus className="mono mb-5 mt-1 w-full rounded-sm border border-[#E2E8F0] bg-white px-3 py-2 text-center text-lg tracking-[0.5em] outline-none focus:border-blue-600" />
            </>
          )}
          {err && <div data-testid="login-error" className="mb-3 rounded-sm border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-600">{err}</div>}
          <button data-testid="login-submit-button" disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-sm bg-blue-600 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-60">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}{mfa ? "Verify" : "Sign in"}
          </button>
          <p className="mt-4 text-[11px] text-slate-500">Belum ada akaun? <Link to="/register" className="text-blue-600 underline" data-testid="login-register-link">Mohon akses</Link> · <Link to="/welcome" className="text-blue-600 underline" data-testid="login-landing-link">Lite Signal</Link></p>
        </form>
      </div>
    </div>
  );
}
